import { useMemo, useState } from 'react';
import { Props as ReactApexChartProps } from 'react-apexcharts';
import { Button } from '../../../../../../components/button/button';
import type { ExternalMonitoringPeriodBounds } from '../../../../../../helpers/external-monitoring-period';
import type {
  AggregationResultsCategoryRow,
  AggregationResultsSentimentRow,
} from '../../../../../../store/api/monitoring-api';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import {
  buildSentimentDynamicsFromRows,
  buildSentimentDynamicsFromSentimentRows,
} from './build-sentiment-dynamics-from-rows';
import { externalMonitoringLineChartInteractions } from './line-chart-interaction-options';
import type {
  AspectsDynamicsGroupingInterval,
  AspectsDynamicsSentimentKey,
} from './build-aspects-dynamics-by-category-chart';
import s from './use-aspects-dynamics-by-category-line.module.css';

const EMPTY_ROWS: AggregationResultsCategoryRow[] = [];
const EMPTY_SENTIMENT: AggregationResultsSentimentRow[] = [];

export type SentimentDynamicsLineInput = {
  rows: AggregationResultsCategoryRow[];
  /** Если задано — график по GET aggregation/results?type=sentiment */
  sentimentRows?: AggregationResultsSentimentRow[];
  groupingInterval: AspectsDynamicsGroupingInterval;
};

export const useSentimentDynamicsLine = (
  _period?: ExternalMonitoringPeriodBounds,
  input?: SentimentDynamicsLineInput,
): ChartProps => {
  const [sentiment, setSentiment] = useState<AspectsDynamicsSentimentKey>('negative');

  const rows = input?.rows ?? EMPTY_ROWS;
  const sentimentRows = input?.sentimentRows ?? EMPTY_SENTIMENT;
  const groupingInterval = input?.groupingInterval ?? 'day';

  const { xCategories, series } = useMemo(() => {
    if (input == null) {
      return { xCategories: [], series: [] as { name: string; data: number[] }[] };
    }
    if (sentimentRows.length > 0) {
      return buildSentimentDynamicsFromSentimentRows(sentimentRows, groupingInterval, sentiment);
    }
    if (rows.length === 0) {
      return { xCategories: [], series: [] as { name: string; data: number[] }[] };
    }
    return buildSentimentDynamicsFromRows(rows, groupingInterval, sentiment);
  }, [input, rows, sentimentRows, groupingInterval, sentiment]);

  const lineColor =
    sentiment === 'positive' ? '#12B76A' : sentiment === 'neutral' ? '#667085' : '#F04438';

  const chartData = useMemo<ReactApexChartProps>(() => {
    return {
      series,
      options: {
        chart: {
          type: 'line',
          ...externalMonitoringLineChartInteractions,
        },
        stroke: { curve: 'smooth', width: 2 },
        noData: {
          text: 'Нет данных за выбранный период',
          align: 'center',
        },
        xaxis: {
          categories: xCategories,
          title: { text: 'Период' },
        },
        yaxis: {
          min: 0,
          title: { text: 'Количество' },
          labels: {
            formatter: (val: number) => String(Math.round(val)),
          },
        },
        colors: [lineColor],
        legend: { position: 'top' },
        grid: { xaxis: { lines: { show: true } }, yaxis: { lines: { show: true } } },
        tooltip: {
          y: {
            formatter: (val: number) => String(Math.round(val)),
          },
        },
      },
    };
  }, [series, xCategories, lineColor]);

  const footer = (
    <div className={s.footerRow}>
      <span className={s.toneLabel}>Тональность:</span>
      {(
        [
          { key: 'negative' as const, label: 'Негативно' },
          { key: 'neutral' as const, label: 'Нейтрально' },
          { key: 'positive' as const, label: 'Позитивно' },
        ] as const
      ).map((item) => (
        <Button
          key={item.key}
          type="button"
          variant="secondary"
          size="sm"
          outline
          className={item.key === sentiment ? s.toneBtnActive : undefined}
          onClick={() => setSentiment(item.key)}
        >
          {item.label}
        </Button>
      ))}
    </div>
  );

  return {
    ...chartData,
    label: 'Динамика тональности',
    labelTooltip:
      'Динамика количества комментариев за выбранный период с разбивкой по типу общей тональности (положительная, нейтральная, отрицательная).',
    labelInfoIcon: true,
    type: 'line',
    footer,
  };
};
